import { DecoratorBlockNode } from "@lexical/react/LexicalDecoratorBlockNode";
import { UnreachableCaseError } from "../../../../../utils/errors/unreachable";
import { uuidv4 } from "../../../../../utils/uuid";
import { headlessDecorate } from "../headless-decorate";
import { convertPollElement, isPollElement } from "./export";

import type { UUID } from "../../../../../utils/uuid";
import type { PollNodeAnswerType, SerializedPollNode } from "./types";
import type { DOMConversionMap, DOMExportOutput, ElementFormatType } from "lexical";

export class PollNode extends DecoratorBlockNode {
  __poll_id: UUID;
  __answer_type: PollNodeAnswerType;
  __refresh_id: UUID;

  constructor(
    poll_id: UUID,
    answer_type: PollNodeAnswerType,
    format?: ElementFormatType,
    key?: string,
  ) {
    super(format, key);
    this.__poll_id = poll_id;
    this.__answer_type = answer_type;
    this.__refresh_id = uuidv4();
  }

  static getType(): string {
    return "poll";
  }

  static clone(node: PollNode): PollNode {
    return new this(node.__poll_id, node.__answer_type, node.__format, node.__key);
  }

  static importJSON(serializedNode: SerializedPollNode): PollNode {
    switch (serializedNode.version) {
      case 1:
        return new this(
          // oxlint-disable-next-line typescript/no-unsafe-type-assertion
          serializedNode.poll_id as UUID,
          {
            type: "custom",
            alignment: serializedNode.alignment,
            button_type: serializedNode.button_type,
          },
          serializedNode.format,
        );
      case 2:
        return new this(
          // oxlint-disable-next-line typescript/no-unsafe-type-assertion
          serializedNode.poll_id as UUID,
          serializedNode.answer_type,
          serializedNode.format,
        );
      default:
        throw new UnreachableCaseError(serializedNode);
    }
  }

  decorate(): React.JSX.Element {
    return headlessDecorate();
  }

  exportJSON(): SerializedPollNode {
    return {
      ...super.exportJSON(),
      poll_id: this.__poll_id,
      answer_type: this.__answer_type,
      type: "poll",
      version: 2,
    };
  }

  createDOM(): HTMLElement {
    const dom = document.createElement("div");
    dom.setAttribute("data-poll-id", this.__poll_id);
    dom.setAttribute("data-poll-answer_type", JSON.stringify(this.__answer_type));
    dom.classList.add("poll-node");
    return dom;
  }

  exportDOM(): DOMExportOutput {
    const dom = document.createElement("div");
    dom.setAttribute("data-poll-id", this.__poll_id);
    dom.setAttribute("data-node-type", "poll-node");
    dom.setAttribute("data-poll-answer_type", JSON.stringify(this.__answer_type));
    return { element: dom };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      div: (node: HTMLElement) => {
        if (!isPollElement(node)) return null;

        return {
          conversion: (n) => convertPollElement(n, PollNode),
          priority: 0,
        };
      },
    };
  }

  getPollId(): UUID {
    return this.getLatest().__poll_id;
  }

  setPollId(poll_id: UUID): void {
    const writable = this.getWritable();
    writable.__poll_id = poll_id;
  }

  getAnswerType(): PollNodeAnswerType {
    return this.getLatest().__answer_type;
  }

  setAnswerType(answer_type: PollNodeAnswerType): void {
    const writable = this.getWritable();
    writable.__answer_type = answer_type;
  }

  refresh(): void {
    const writable = this.getWritable();
    writable.__refresh_id = uuidv4();
  }
}
